/**
 * Opening and closing times are offered as a dropdown cut on the venue's slot granularity, so a
 * 30-minute venue can only open on :00/:30 and a 60-minute one only on the hour. Keeping the two
 * in step is what stops an operator from setting hours that no booking slot can ever start on.
 */

const MINUTES_IN_DAY = 24 * 60;

function asClock(minutesOfDay: number): string {
  const hours = Math.floor(minutesOfDay / 60);
  const minutes = minutesOfDay % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Minutes since midnight of an `HH:mm` time, or `null` when the value is blank or malformed. */
function minutesOfDay(time: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/** Every time on the granularity grid, from 00:00 up to the last slot before midnight. */
export function slotTimeOptions(granularityMinutes: number): string[] {
  const slots: string[] = [];
  for (let minute = 0; minute < MINUTES_IN_DAY; minute += granularityMinutes) {
    slots.push(asClock(minute));
  }
  return slots;
}

/**
 * Round an already-picked time down onto the grid. Changing granularity can strand a time that
 * is no longer offered (09:30 is not on the 60-minute grid); snapping down keeps the field
 * showing a value the dropdown can actually display. A blank stays blank.
 */
export function snapToSlotGrid(time: string, granularityMinutes: number): string {
  const minutes = minutesOfDay(time);
  if (minutes === null) return '';
  return asClock(Math.floor(minutes / granularityMinutes) * granularityMinutes);
}

/**
 * Whether an `HH:mm` time lies on the granularity grid. Operating hours must open and close on
 * it (ADR 0081); 00:00 is on every grid, so a midnight close always passes.
 */
export function isOnSlotGrid(time: string, granularityMinutes: number): boolean {
  const minutes = minutesOfDay(time);
  return minutes !== null && minutes % granularityMinutes === 0;
}
